import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UiButton, UiIcon } from 'ui-components';
import { toast } from 'lib';
import { isAppLang } from 'utils';
import { usePermission } from 'hooks';
import {
  store,
  fetchExcelExportLimits,
  createExcelExport,
  createExcelTitle,
  changeExportvalue,
  resetExcelExport,
  openBulletinContentQueryModal,
  closeBulletinContentQueryModal,
  fetchBulletinListForExport,
  addContentToBulletinByQuery,
  resetBulletinContentQueryForm,
  changeBulletinContentQueryValue,
} from 'store';
import { FLOW_STEPS } from './contentExport.constants';

const resolveExportOverrides = (value) => {
  if (!value || typeof value !== 'object' || value.nativeEvent) {
    return {};
  }
  if (!value.page && !value.platform) {
    return {};
  }
  return { page: value.page, platform: value.platform };
};

const useContentExport = ({ platform, excelOnly = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const { userPermission } = usePermission();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flowStep, setFlowStep] = useState(FLOW_STEPS.TYPE);

  const excelStep = useSelector((state) => state.excelExportReducer.step);
  const excelPage = useSelector((state) => state.excelExportReducer.page);
  const excelPlatform = useSelector(
    (state) => state.excelExportReducer.platform
  );
  const excelFields = useSelector(
    (state) => state.excelExportReducer.excelFields
  );
  const excelCount = useSelector((state) => state.excelExportReducer.count);
  const excelLimits = useSelector((state) => state.excelExportReducer.limits);
  const excelLimitsLoading = useSelector(
    (state) => state.excelExportReducer.limits.loading
  );
  const excelLoading = useSelector(
    (state) => state.excelExportReducer.export.loading
  );
  const excelError = useSelector(
    (state) => state.excelExportReducer.export.error
  );
  const currentQuery = useSelector((state) => state.pageReducer.currentQuery);

  const bulletinActiveStep = useSelector(
    (state) => state.bulletinContentQueryReducer.activeStep
  );
  const selectedBulletinId = useSelector(
    (state) => state.bulletinContentQueryReducer.selectedBulletinId
  );
  const bulletinCount = useSelector(
    (state) => state.bulletinContentQueryReducer.count
  );
  const bulletinList = useSelector(
    (state) => state.bulletinContentQueryReducer.bulletinList
  );
  const bulletinSubmit = useSelector(
    (state) => state.bulletinContentQueryReducer.submit
  );

  const { permission: permiExportExcel } = useMemo(
    () => userPermission('Excel', 'store'),
    [userPermission]
  );

  const { permission: permiExportExcelResource } = useMemo(
    () => userPermission('ExcelResource', 'store'),
    [userPermission]
  );

  const showExcel = permiExportExcel || permiExportExcelResource;

  const showBulletin = useMemo(() => {
    const { permission } = userPermission('Bulletin', 'addToBulletin');
    return (
      permission &&
      !isAppLang('ar') &&
      !excelOnly &&
      platform !== 'multiPlatform'
    );
  }, [excelOnly, platform, userPermission]);

  const { permission: permiBulletinList } = useMemo(
    () => userPermission('Bulletin', 'index'),
    [userPermission]
  );

  const canSelectType = showExcel && showBulletin;

  const hasExcelFields = Boolean(
    excelPage &&
      excelPlatform &&
      Object.keys(excelFields[excelPage][excelPlatform] || []).length > 0
  );

  const changeExcelStep = (step) => {
    dispatch(
      changeExportvalue({
        key: 'step',
        value: step,
      })
    );
  };

  const goToTypeStep = () => {
    setFlowStep(FLOW_STEPS.TYPE);
    dispatch(closeBulletinContentQueryModal());
    changeExcelStep('step_1');
  };

  const openExcelStep = (overrides = {}) => {
    const excelState = store.getState().excelExportReducer;
    const page = overrides.page || excelState.page || excelPage;
    const exportPlatform =
      overrides.platform || excelState.platform || excelPlatform;
    dispatch(
      changeExportvalue({
        key: 'title',
        value: exportPlatform
          ? createExcelTitle(currentQuery?.queryId, page, exportPlatform)
          : '',
      })
    );
    dispatch(resetExcelExport());
    dispatch(fetchExcelExportLimits({ page }))
      .unwrap()
      .catch(() => {
        toast({
          title: t('common:error'),
          description: t('content:error.fetchExcelExportLimits'),
          type: 'error',
        });
      });
    setFlowStep(FLOW_STEPS.EXCEL);
  };

  const openBulletinStep = () => {
    dispatch(resetBulletinContentQueryForm());
    dispatch(
      changeBulletinContentQueryValue({
        key: 'count',
        value: '',
      })
    );
    dispatch(openBulletinContentQueryModal({ platform }));
    setFlowStep(FLOW_STEPS.BULLETIN);

    dispatch(fetchBulletinListForExport())
      .unwrap()
      .catch(() => {
        toast({
          title: t('common:error'),
          description: t('content:error.bulletinExport.fetchBulletinList'),
          type: 'error',
        });
      });
  };

  const handleOpen = (overrides) => {
    setIsModalOpen(true);
    const exportOverrides = resolveExportOverrides(overrides);

    if (canSelectType) {
      setFlowStep(FLOW_STEPS.TYPE);
      return;
    }

    if (showExcel) {
      openExcelStep(exportOverrides);
      return;
    }

    if (showBulletin) {
      openBulletinStep();
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setFlowStep(FLOW_STEPS.TYPE);
    dispatch(closeBulletinContentQueryModal());
    changeExcelStep('step_1');
  };

  const handleTypeSelect = (type) => {
    if (type === 'excel') {
      openExcelStep();
      return;
    }

    if (type === 'bulletin') {
      openBulletinStep();
    }
  };

  const handleExcelSubmit = async () => {
    try {
      await dispatch(createExcelExport({ page: excelPage })).unwrap();
      handleClose();
      toast({
        title: t('content:success.createExcelExport.title', {
          format: 'Excel',
        }),
        description: t('content:success.createExcelExport.description', {
          format: 'Excel',
        }),
        type: 'info',
        icon: <UiIcon icon='ri-file-download-line' color='#121212' size={18} />,
      });
    } catch (e) {
      toast({
        title: t('content:error.createExcelExport.title', {
          format: 'Excel',
        }),
        description: t('content:error.createExcelExport.description', {
          format: 'Excel',
        }),
        type: 'error',
        icon: <UiIcon icon='ri-file-download-line' color='#121212' size={18} />,
      });
    }
  };

  const handleBulletinSubmit = async () => {
    try {
      await dispatch(
        addContentToBulletinByQuery({
          platform,
          count: Number(bulletinCount),
          bulletinId: selectedBulletinId,
        })
      ).unwrap();

      const bulletin = bulletinList.data?.find(
        (item) => item.id === selectedBulletinId
      );
      handleClose();
      toast({
        title: t('content:success.bulletinExport.title'),
        description: t('content:success.bulletinExport.description'),
        type: 'success',
        action: (
          <div className='actions'>
            <UiButton variant='text'>
              <Link
                to={`/bulletins/${bulletin?.id}/${platform}`}
                state={{
                  title: bulletin?.title
                    ?.replaceAll(' ', '_')
                    .replaceAll('/', '-'),
                }}
              >
                {t('content:bulletinExport.viewBulletin')}
              </Link>
            </UiButton>
            {permiBulletinList && (
              <UiButton variant='text'>
                <Link to='/bulletins'>
                  {t('content:bulletinExport.bulletinList')}
                </Link>
              </UiButton>
            )}
          </div>
        ),
      });
    } catch (error) {
      // Error toast is shown in addContentToBulletinByQuery
    }
  };

  const activeStepKey = useMemo(() => {
    if (flowStep === FLOW_STEPS.TYPE) return 'type';
    if (flowStep === FLOW_STEPS.EXCEL) {
      return excelStep === 'step_2' ? 'excelAdvanced' : 'excel';
    }
    if (bulletinActiveStep === 'createBulletin') return 'createBulletin';
    return 'bulletin';
  }, [bulletinActiveStep, excelStep, flowStep]);

  return {
    isModalOpen,
    showExcel,
    showBulletin,
    canSelectType,
    hasExcelFields,
    activeStepKey,
    excelError,
    excelLoading,
    excelLimitsLoading,
    excelCount,
    excelLimits,
    bulletinSubmit,
    selectedBulletinId,
    bulletinCount,
    bulletinList,
    handleOpen,
    handleClose,
    handleTypeSelect,
    handleExcelSubmit,
    handleBulletinSubmit,
    goToTypeStep,
    hasError,
    setHasError,
  };
};

export default useContentExport;
